import { CSSProperties, ReactNode, useEffect, useMemo, useRef } from "react";
import { useVirtualizer, VirtualItem } from "@tanstack/react-virtual";
import { Box, Skeleton, SxProps, Theme } from "@mui/material";
import {
  CARDS_IN_DOM_UP_AND_DOWN,
  ColumnConfig,
  DEFAULT_COLUMN_CONFIG,
  ESTIMATE_HEIGHT_CARD,
  getColumnCountFromConfig,
} from "@components/virtualized/virtualized-grid-utils";
import { useMediaQueryList } from "@hooks/use-media-query";

interface VirtualizedGridProps<T> {
  /** List of items to render inside the virtualized grid */
  items: T[];

  /** Flag indicating if the grid is currently loading more items */
  isLoading: boolean;

  /** Function called to load more items when scrolling reaches the end */
  loadMoreItems?: () => void;

  /** Function to render each item */
  renderItem: (item: T, index: number) => ReactNode;

  /**
   * Height of the container that holds the virtualized grid.
   * @default "100%"
   */
  height?: string;

  /** Function to render a skeleton while loading items */
  renderSkeletonItem: (index: number) => ReactNode;

  /**
   * Estimated size (height) of each row/item in pixels.
   * @default 314
   */
  estimateSize?: number;

  /**
   * Number of extra rows to render above and below the visible viewport.
   * @default 5
   */
  overscan?: number;

  /** Configuration object for determining column count based on breakpoints */
  columnConfig?: ColumnConfig;

  /** Optional footer action renderer. Shown sticky at the bottom. */
  renderFooterAction?: () => JSX.Element;
}

/**
 * A responsive virtualized grid component optimized for performance with large lists.
 *
 * @template T The type of each item in the `items` array
 */
export function VirtualizedGrid<T>({
  items,
  isLoading,
  loadMoreItems,
  renderItem,
  height = "100%",
  renderSkeletonItem,
  estimateSize = ESTIMATE_HEIGHT_CARD,
  overscan = CARDS_IN_DOM_UP_AND_DOWN,
  columnConfig = DEFAULT_COLUMN_CONFIG,
  renderFooterAction,
}: VirtualizedGridProps<T>): JSX.Element {
  const containerRef = useRef<HTMLDivElement>(null);
  const isFetchingRef = useRef(false);

  const {
    isScreenXlOrLarger,
    isScreenLgOrLarger,
    isScreenMdOrLarger,
    isScreenSmOrLarger,
    isScreenXXL,
    isScreenXXXL,
  } = useMediaQueryList();

  const columnCount = getColumnCountFromConfig(columnConfig, {
    isScreenXXXL,
    isScreenXXL,
    isXL: isScreenXlOrLarger,
    isLG: isScreenLgOrLarger,
    isMD: isScreenMdOrLarger && !isScreenLgOrLarger,
    isSM: isScreenSmOrLarger && !isScreenMdOrLarger,
  });

  /**
   * Default row sx: grid layout with dynamic columns.
   */
  const defaultRowSx: SxProps<Theme> = {
    display: "grid",
    gridTemplateColumns: `repeat(${columnCount}, minmax(0, 1fr))`,
    gap: columnCount > 1 ? 2 : 0,
    position: "absolute",
    width: "100%",
    // Fixed row height for predictable virtualization and performance
    height: estimateSize,
    // Using translate3d triggers GPU acceleration for smoother scrolling compared to translateY
    transform: "translate3d(0, var(--virtualization-row), 0)",
    willChange: "transform",
  };

  /**
   * Returns the list rendered by the VirtualGrid
   * When loading, adds `columnCount * 2` null items so the grid shows two
   * full rows of skeletons (passed via props) and the layout doesn't jump
   */
  const displayItems = useMemo(() => {
    return isLoading ? [...items, ...Array(columnCount * 2).fill(null)] : items;
  }, [items, isLoading, columnCount]);

  const rowCount = Math.ceil(displayItems.length / columnCount);

  /** Virtualizer instance for row virtualization */
  const rowVirtualizer = useVirtualizer({
    count: rowCount,
    getScrollElement: () => containerRef.current,
    estimateSize: () => estimateSize,
    overscan,
  });

  const virtualItems = rowVirtualizer.getVirtualItems();

  /**
   * Triggers `loadMoreItems` when the user scrolls near the end of the list.
   * Prevents duplicate calls using `isFetchingRef`.
   */
  useEffect(() => {
    if (!loadMoreItems || isLoading) {
      return;
    }

    const lastVirtual = virtualItems.at(-1);
    if (!lastVirtual) {
      // No virtualized items available
      return;
    }

    const lastRealRowIndex = Math.ceil(items.length / columnCount) - 1;

    const shouldTrigger =
      lastVirtual.index >= lastRealRowIndex &&
      !isLoading &&
      !isFetchingRef.current;

    if (!shouldTrigger) {
      return;
    }

    isFetchingRef.current = true;

    Promise.resolve(loadMoreItems())
      .catch(() => {
        // Ignore errors — this component only triggers load
      })
      .finally(() => {
        isFetchingRef.current = false;
      });

    return () => {
      // Cleanup: reset fetching flag if component unmounts during load
      isFetchingRef.current = false;
    };
  }, [items.length, columnCount, isLoading, loadMoreItems, virtualItems]);

  /**
   * Render a single cell (item or skeleton)
   */
  function renderCell(item: T | null, index: number): ReactNode {
    return item ? renderItem(item, index) : renderSkeletonItem(index);
  }

  return (
    <Box
      sx={{
        height,
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          flex: 1,
          overflow: "auto",
          contain: "strict",
          p: "1px",
          minHeight: 0,
        }}
      >
        <Box
          sx={{
            height: `${rowVirtualizer.getTotalSize()}px`,
            position: "relative",
            width: "100%",
            flex: "0 0 auto",
          }}
        >
          {virtualItems.map((virtualRow: VirtualItem) => {
            const rowIndex = virtualRow.index;
            const startIndex = rowIndex * columnCount;

            return (
              <Box
                key={virtualRow.key}
                sx={defaultRowSx}
                style={
                  {
                    // CSS variable for position to avoid recalculating sx on every render
                    // eslint-disable-next-line @typescript-eslint/naming-convention
                    "--virtualization-row": `${virtualRow.start}px`,
                  } as CSSProperties
                }
              >
                {Array.from({ length: columnCount }).map((_, colIndex) => {
                  const itemIndex = startIndex + colIndex;
                  const item = displayItems[itemIndex];

                  if (itemIndex >= displayItems.length) {
                    return null;
                  }

                  return renderCell(item, itemIndex);
                })}
              </Box>
            );
          })}
        </Box>
        {/* Fixed button at bottom */}
        {renderFooterAction && (
          <Box
            sx={{
              position: "sticky",
              bottom: "-1px",
              border: "4px solid var(--app-background-color)",
            }}
          >
            {/* Show skeleton only on first load */}
            {items.length === 0 && isLoading ? (
              <Skeleton variant="rectangular" height={36} />
            ) : (
              renderFooterAction()
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
}
